// frontend/src/CalendarBubble.js
import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { addMonths, subMonths, format as fmt } from 'date-fns'
import WorkWeekMonthView from './WorkWeekMonthView'
import './CalendarBubble.css'

function CalendarBubble({ meetingType, onConfirm, onCancel }) {
  const today = new Date()

  // === Calendar state ===
  const [calendarMonth, setCalendarMonth] = useState(today)
  const [selectedDate, setSelectedDate]   = useState(today)

  // === Time‐slot state ===
  const [availableTimes, setAvailableTimes] = useState([])
  const [selectedTime, setSelectedTime]     = useState(null)

  // === Hard-coded timezone ===
  const calendarTimezone = 'Australia/Brisbane'

  // === Loading & errors ===
  const [error, setError]     = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? ''

  // Helper to format date as YYYY-MM-DD
  const formatDate = (d) => {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  // Fetch slots from backend
  const fetchTimes = (dateObj) => {
    if (!meetingType) {
      setError('Missing meetingType')
      return
    }
    const dateStr = formatDate(dateObj)
    setIsLoading(true)
    setError(null)
    setAvailableTimes([])
    setSelectedTime(null)

    axios
      .get(`${BACKEND_URL}/api/chatbot/get_available_times`, {
        params: { date: dateStr, meeting_type: meetingType },
      })
      .then((res) => {
        setAvailableTimes(res.data.available_slots || [])
        // timezone is now hard-coded above
      })
      .catch((err) => {
        setError(err.response?.data?.detail || 'Could not fetch times.')
        setAvailableTimes([])
      })
      .finally(() => setIsLoading(false))
  }

  // On initial mount & when selectedDate changes
  useEffect(() => {
    fetchTimes(selectedDate)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, meetingType, BACKEND_URL])

  // Month navigation
  const prevMonth = () => setCalendarMonth((m) => subMonths(m, 1))
  const nextMonth = () => setCalendarMonth((m) => addMonths(m, 1))

  // Confirm the chosen slot
  const handleConfirm = () => {
    if (!selectedTime) {
      alert('Please select a time slot.')
      return
    }
    const dateStr = formatDate(selectedDate)
    onConfirm(`${dateStr}T${selectedTime}:00`)
  }

  return (
    <div className="calendar-bubble-container">
      {/* Header */}
      <div className="calendar-bubble-header">
        <h4>Select Date &amp; Time ({calendarTimezone})</h4>
        <div className="ww-nav">
          <button onClick={prevMonth}>&lt;</button>
          <span className="ww-month-title">
            {fmt(calendarMonth, 'MMMM yyyy')}
          </span>
          <button onClick={nextMonth}>&gt;</button>
        </div>
      </div>

      {/* Month‐grid (Mon–Fri only) */}
      <div className="calendar-bubble-content">
        <WorkWeekMonthView
          month={calendarMonth}
          selected={selectedDate}
          onSelect={(day) => setSelectedDate(day)}
        />

        {isLoading && <p>Loading times...</p>}
        {error && <p className="error-text">{error}</p>}

        {!isLoading && !error && (
          <div className="time-slot-list">
            {availableTimes.map((t) => (
              <button
                key={t}
                className={
                  t === selectedTime
                    ? 'time-slot-btn selected'
                    : 'time-slot-btn'
                }
                onClick={() => setSelectedTime(t)}
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="calendar-bubble-footer">
        <button onClick={onCancel}>Cancel</button>
        <button
          onClick={handleConfirm}
          disabled={!selectedTime || isLoading}
        >
          Confirm
        </button>
      </div>
    </div>
  )
}

export default CalendarBubble
